import { Box, useToast } from '@chakra-ui/react';
import { BackofficeGetGroupedPromosProductHighlightResponse } from '@diamond/shared/types';
import { showToast, SortableTable, TableColumn } from '@diamond/shared/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { BACKOFFICE_GROUP_PROMO_HIGHLIGHT_QUERY_KEY } from '../../api/get-group-promo-highlight-list';
import { useReorderProductHighlightMutation } from '../../api/reorder-product-highlight';

type ProductHighlightItem =
  BackofficeGetGroupedPromosProductHighlightResponse[number];

interface ProductHighlightProps {
  data: ProductHighlightItem[];
  promoId: string;
}

/**
 * ProductHighlight component displays a sortable list of products highlighted in a promotion
 * Allows admin users to reorder products via drag and drop functionality
 *
 * @param data - Array of product highlight items to display
 * @param promoId - UUID of the parent promotion
 */
export default function ProductHighlight({
  data,
  promoId,
}: ProductHighlightProps) {
  const [items, setItems] = useState<ProductHighlightItem[]>(data);
  const toast = useToast();
  const queryClient = useQueryClient();
  useEffect(() => {
    setItems(data);
  }, [data]);

  const reorderProductHighlightMutation = useReorderProductHighlightMutation();

  const columns: TableColumn[] = [
    {
      key: 'no',
      header: 'No.',
      width: '80px',
      textAlign: 'center',
      cell: (_item, index) => index + 1,
    },
    {
      key: 'dragHandle',
      header: 'Urutan',
      width: '100px',
      textAlign: 'center',
    },
    {
      key: 'name',
      header: 'Nama Produk',
      cell: (item: ProductHighlightItem) => {
        return `${item.title} (${item.product_item_code})`;
      },
    },
  ];

  /**
   * Handles reordering of products after drag and drop
   * Calls API to persist the new order in the backend
   *
   * @param productItemCode - The item code of the product being moved
   * @param newIndex - The new position (0-based index) for the product
   */
  const handleOnReorder = (productItemCode: number, newIndex: number) => {
    reorderProductHighlightMutation.mutate(
      {
        id: promoId,
        index: newIndex,
        product_item_code: productItemCode,
      },
      {
        onError: (error) => {
          console.error('Failed to reorder product highlight:', error);
          showToast(
            toast,
            'error',
            error.message || 'Gagal Mengurutkan Highlight produk'
          );
          setItems(data);
        },
        onSuccess: () => {
          showToast(
            toast,
            'success',
            'Berhasil Merubah Urutan Highlight Produk'
          );
        },
        onSettled: () => {
          queryClient.invalidateQueries({
            queryKey: [
              BACKOFFICE_GROUP_PROMO_HIGHLIGHT_QUERY_KEY,
              items[0]?.group_id,
            ],
          });
        },
      }
    );
  };

  return (
    <Box>
      <SortableTable
        items={items}
        columns={columns}
        droppableId={`product-highlight-table-${promoId}`}
        onReorder={handleOnReorder}
        dragHandleColumn={1}
        headerBgColor="#0000A3"
        headerColor={'white'}
        tableVariant="simple"
      />
    </Box>
  );
}
